import { addDays, startOfWeek, type TimeEntry } from '@coneno/c-sheet-shared'

export type ReportGrouping = 'project' | 'person'

export type ReportLevel = 'project' | 'task' | 'person'

export type ReportGroup = {
  /** The project, task or person id; an empty string for entries without a task. */
  id: string
  level: ReportLevel
  minutes: number
  billableMinutes: number
  entries: number
  children: ReportGroup[]
}

export type ReportTotals = {
  minutes: number
  billableMinutes: number
  entries: number
}

export function reportTotals(entries: TimeEntry[]): ReportTotals {
  return entries.reduce(
    (totals, entry) => ({
      minutes: totals.minutes + entry.durationMinutes,
      billableMinutes: totals.billableMinutes + (entry.billable ? entry.durationMinutes : 0),
      entries: totals.entries + 1
    }),
    { minutes: 0, billableMinutes: 0, entries: 0 }
  )
}

/** The levels of the breakdown, outermost first. */
export function reportLevels(grouping: ReportGrouping): ReportLevel[] {
  return grouping === 'project' ? ['project', 'task', 'person'] : ['person', 'project', 'task']
}

function levelId(entry: TimeEntry, level: ReportLevel): string {
  if (level === 'project') return entry.projectId
  if (level === 'person') return entry.personId
  return entry.taskId ?? ''
}

function groupLevels(entries: TimeEntry[], levels: ReportLevel[]): ReportGroup[] {
  const [level, ...rest] = levels
  if (!level) return []
  const groups = new Map<string, TimeEntry[]>()
  for (const entry of entries) {
    const id = levelId(entry, level)
    groups.set(id, [...(groups.get(id) ?? []), entry])
  }
  return Array.from(groups, ([id, groupEntries]) => ({
    id,
    level,
    ...reportTotals(groupEntries),
    children: groupLevels(groupEntries, rest)
  })).sort((left, right) => right.minutes - left.minutes)
}

/**
 * Groups entries by project (then task, then person) or by person (then project, then task),
 * largest first at every level. Minutes are summed exactly; formatting happens in the interface.
 */
export function groupReport(entries: TimeEntry[], grouping: ReportGrouping): ReportGroup[] {
  return groupLevels(entries, reportLevels(grouping))
}

export type TaskSlice = {
  projectId: string
  taskId: string | null
  minutes: number
}

/**
 * Minutes per project and task, grouped by project (largest project first) with each project's
 * tasks largest first. Entries without a task form their own slice with `taskId` null.
 */
export function splitByTask(entries: TimeEntry[]): TaskSlice[] {
  const projects = new Map<string, { minutes: number; tasks: Map<string | null, number> }>()
  for (const entry of entries) {
    const project = projects.get(entry.projectId) ?? { minutes: 0, tasks: new Map() }
    project.minutes += entry.durationMinutes
    project.tasks.set(entry.taskId, (project.tasks.get(entry.taskId) ?? 0) + entry.durationMinutes)
    projects.set(entry.projectId, project)
  }
  return Array.from(projects.entries())
    .sort(([, left], [, right]) => right.minutes - left.minutes)
    .flatMap(([projectId, project]) =>
      Array.from(project.tasks.entries(), ([taskId, minutes]) => ({
        projectId,
        taskId,
        minutes
      })).sort((left, right) => right.minutes - left.minutes)
    )
}

export type TimeBucket = { start: string; minutes: number }

/** Longest range still shown day by day; longer ranges are shown week by week. */
const maxDailyBuckets = 62

/**
 * Minutes per day from `from` to `to`, including days without entries. Ranges longer than
 * two months are bucketed by week (starting Monday) so the chart stays readable.
 */
export function minutesOverTime(
  entries: TimeEntry[],
  from: string,
  to: string
): { unit: 'day' | 'week'; buckets: TimeBucket[] } {
  let days = 0
  for (let date = from; date <= to && days <= maxDailyBuckets; date = addDays(date, 1)) days += 1
  const unit = days > maxDailyBuckets ? 'week' : 'day'
  const bucketStart = (date: string): string => (unit === 'week' ? startOfWeek(date) : date)
  const buckets = new Map<string, TimeBucket>()
  for (let date = bucketStart(from); date <= to; date = addDays(date, unit === 'week' ? 7 : 1)) {
    buckets.set(date, { start: date, minutes: 0 })
  }
  for (const entry of entries) {
    const bucket = buckets.get(bucketStart(entry.workDate))
    if (bucket) bucket.minutes += entry.durationMinutes
  }
  return { unit, buckets: Array.from(buckets.values()) }
}
